import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PrivateRoute } from './components/PrivateRoute';
import { Sentry } from './config/sentry';
import { AuthProvider } from './contexts/AuthContext';
import { BrandProvider } from './contexts/BrandContext';
import { MainLayout } from './layouts/MainLayout';
import { PlatformLayout } from './layouts/PlatformLayout';
import { getEnvironmentPrefix } from './utils/environment';

function ErrorFallback({ error: _error, resetError }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2,
        p: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="h5" color="error">
        エラーが発生しました
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
        予期しないエラーが発生しました。問題が解決しない場合は、サポートまでお問い合わせください。
      </Typography>
      <Button variant="contained" onClick={resetError}>
        再読み込み
      </Button>
    </Box>
  );
}

const ChangePasswordPage = lazy(() =>
  import('./pages/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage }))
);
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const KnowledgeManagementPage = lazy(() =>
  import('./pages/KnowledgeManagementPage').then((m) => ({ default: m.KnowledgeManagementPage }))
);
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const OrgEditPage = lazy(() => import('./pages/OrgEditPage').then((m) => ({ default: m.OrgEditPage })));
const OrgListPage = lazy(() => import('./pages/OrgListPage').then((m) => ({ default: m.OrgListPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SupportHistoryPage = lazy(() =>
  import('./pages/SupportHistoryPage').then((m) => ({ default: m.SupportHistoryPage }))
);
const SystemSettingsPage = lazy(() =>
  import('./pages/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage }))
);
const TenantEditPage = lazy(() => import('./pages/TenantEditPage').then((m) => ({ default: m.TenantEditPage })));
const TenantInfoPage = lazy(() => import('./pages/TenantInfoPage').then((m) => ({ default: m.TenantInfoPage })));
const ThreadDetailPage = lazy(() => import('./pages/ThreadDetailPage').then((m) => ({ default: m.ThreadDetailPage })));
const ThreadListPage = lazy(() => import('./pages/ThreadListPage').then((m) => ({ default: m.ThreadListPage })));

function App({ brandConfig, theme, logo }) {
  useEffect(() => {
    const prefix = getEnvironmentPrefix();
    if (prefix) {
      document.title = `${prefix} ${brandConfig.name}`;
    } else {
      document.title = brandConfig.name;
    }
  }, [brandConfig.name]);

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <ThemeProvider theme={theme}>
        <BrandProvider brandConfig={brandConfig} logo={logo}>
          <AuthProvider>
            <BrowserRouter>
              <Suspense
                fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <CircularProgress />
                  </Box>
                }
              >
                <Routes>
                  <Route path="/login" element={<LoginPage />} />

                  <Route element={<PrivateRoute roles={['swandive']} />}>
                    <Route path="/ops" element={<PlatformLayout />}>
                      <Route index element={<OrgListPage />} />
                      <Route path="organizations/new" element={<OrgEditPage />} />
                      <Route path="organizations/:orgId" element={<OrgEditPage />} />
                      <Route path="organizations/:orgId/tenants/new" element={<TenantEditPage />} />
                      <Route path="organizations/:orgId/tenants/:tenantId" element={<TenantEditPage />} />
                      <Route path="knowledge" element={<KnowledgeManagementPage />} />
                      <Route path="history" element={<SupportHistoryPage />} />
                      <Route path="system-settings" element={<SystemSettingsPage />} />
                    </Route>
                  </Route>

                  <Route element={<PrivateRoute />}>
                    <Route path="/" element={<HomePage />} />
                  </Route>

                  <Route element={<PrivateRoute requirePasswordChange={true} />}>
                    <Route path="/change-password" element={<ChangePasswordPage />} />
                  </Route>

                  <Route element={<PrivateRoute roles={['admin', 'staff']} />}>
                    <Route element={<MainLayout />}>
                      <Route path="/threads" element={<ThreadListPage />} />
                      <Route path="/threads/:threadId" element={<ThreadDetailPage />} />
                      <Route path="/tenants" element={<TenantInfoPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </BrandProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
